import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { CountriesContext } from "@providers/Countries";
import { ISCOArchetypeContext } from "@providers/ISCOArchetype";

import { SkillTypes, WSSkillGroups } from '@helpers/enums';
import * as url from '@api/requests';

import { FaArrowCircleLeft } from "react-icons/fa";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BoltIcon from '@mui/icons-material/Bolt';
import Button from '@components/Button/Button';
import TopSkills from "./TopSkills";
import ExportButton from "@components/ExportButton";
import { DefaultAvatarsClassification } from '@config/avatars-classification';

import LoaderImage from '@assets/loader.gif';

import './SkillsDetails.scss';

type SkillsDetailsProps = {
    handleGoBackToChildISCOChart: () => void;
    countryCode: string;
};

const SkillsDetails = ({ handleGoBackToChildISCOChart, countryCode }: SkillsDetailsProps) => {
    const { countryDetails } = useContext(CountriesContext);
    const { selectedChildISCO } = useContext(ISCOArchetypeContext);

    const [isLoadingSkills, setIsLoadingSkills] = useState<boolean>(false);
    const [topSkills, setTopSkills] = useState<any>(null);
    const [skillType, setSkillType] = useState<string>(SkillTypes.ESCO);
    const [topSkillGroup, setTopSkillGroup] = useState<string>(WSSkillGroups.HardSkill);

    const countryData = useMemo(() => {
        return (
            countryDetails?.find(({ code }: any) => code.toLowerCase() === countryCode) || null
        );
    }, [countryCode, countryDetails]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoadingSkills(true);

            const [errTopSkills, incomingTopSkills] = await url.getTopSkillsByISCO4([countryData.hubId], [selectedChildISCO.isco4_code]);
            if (errTopSkills) console.warn(errTopSkills);
            else setTopSkills && setTopSkills(incomingTopSkills)

            setIsLoadingSkills(false);
        };
        if (selectedChildISCO && selectedChildISCO.isco4_code && countryData && countryData.hubId) {
            fetchData();
        }
    }, [selectedChildISCO, countryData]);

    const handleChangeSkillType = useCallback(() => {
        setSkillType((prevSkillsType: string) => (prevSkillsType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS));
    }, []);


    return <>
        <div className="skills-details-container">
            <div className="skills-details-basic-info">
                <div onClick={handleGoBackToChildISCOChart} className="skills-details-back-button-container">
                    <FaArrowCircleLeft className="skills-details-back-button-arrow" />
                </div>
                {selectedChildISCO && 
                    <>
                        <div className="skills-details-basic-info-image-container">
                        <img src={process.env.REACT_APP_CHAT_ISCO_ARCHETYPE_URL + DefaultAvatarsClassification[countryCode].theme + "/" + DefaultAvatarsClassification[countryCode].region + "/" + DefaultAvatarsClassification[countryCode].ethnicity + "/" + selectedChildISCO.isco4_code + ".png"} width="200px" height="200px" alt="" />
                        </div>
                        {selectedChildISCO.isco4_tasks && <div className="skills-details-basic-info-description">{selectedChildISCO.isco4_tasks}</div>}
                    </>
                }
            </div>
            <div className="skills-details-info">
                {selectedChildISCO &&
                    <div className="skillsDetails occupation-tab">
                        <div className="occupation-tab__top-skills">
                            <h4 className="border-bottom mb-2 occupation-tab__top-skills--header">
                                <div>
                                    {skillType === SkillTypes.WS ?
                                        <span>Top {topSkillGroup + "s"} for {selectedChildISCO.isco4_label}</span>
                                        :
                                        <span>Top Skills in {selectedChildISCO.isco4_label}</span>
                                    }
                                </div>
                                {process.env.REACT_APP_ESCO_ENABLED === "true" &&
                                    <Button 
                                    label={skillType === SkillTypes.WS ? SkillTypes.ESCO : SkillTypes.WS} 
                                    onClick={handleChangeSkillType} 
                                    style={{ display: 'none' }} 
                                />
                                }
                            </h4>
                            <div>
                                {isLoadingSkills ?
                                    <img src={LoaderImage} alt="" className="loadingImg" style={{ width: 60 }} />
                                    :
                                    (topSkills && topSkills.length ?
                                        <div>
                                            <div>
                                                {skillType === SkillTypes.WS ?
                                                    <>
                                                        <HistoryEduIcon className="bar-toggle-icon" id="topSkillsCertification-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.Certification)} />
                                                        <AutoFixHighIcon className="bar-toggle-icon" id="topSkillsSoftSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.SoftSkill)} />
                                                        <BoltIcon className="bar-toggle-icon" id="topSkillsHardSkill-toggle" onClick={() => setTopSkillGroup(WSSkillGroups.HardSkill)} />
                                                    </>
                                                    :
                                                    <div className="d-flex justify-content-center mt-2">
                                                       
                                                    </div>
                                                }
                                            </div>
                                            <ExportButton countryName={countryData.country} exportDOMId="topSkills" urlAPI={url.exportTopSkillsByISCO4} urlDataAPI={{
                                                hubIds: [countryData.hubId],
                                                isco4Codes: [selectedChildISCO.isco4_code],
                                                type: skillType === SkillTypes.WS ? topSkillGroup : SkillTypes.ESCO,
                                            }} />
                                            <div id={"topSkills"} >
                                                <TopSkills topSkills={topSkills.find((ts: any) => (skillType === SkillTypes.WS ? ts.skillGroup === topSkillGroup : ts.skillGroup === SkillTypes.ESCO))?.topSkills || []} skillType={skillType} />
                                            </div>
                                        </div>
                                        :
                                        <div className="na">N/A</div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>


        
    </>
};

export default SkillsDetails;
